import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  subMonths,
  format,
} from 'date-fns'
import { Clock, Eye, X } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  Badge,
  Button,
  DateInput,
  SegmentedControl,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { EmptyState } from '@/components/common/EmptyState'
import {
  TableRowAction,
  TableRowActions,
} from '@/components/common/TableRowActions'
import {
  DataTable,
  DataTableCard,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter'
import { useAttendanceWorkspace } from '@/features/attendance/hooks/useAttendance'
import { formatDate } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import { attendanceStatusBadgeVariant } from '@/features/attendance/utils/attendanceErrors'
import {
  ATTENDANCE_STATUS_LABEL,
  attendanceUserName,
  dayOffset,
  formatWorkedMinutes,
  getScheduledWindow,
  isCurrentlyClockedIn,
} from '@/features/attendance/utils/attendanceDisplay'
import { AttendanceDetailModal } from './AttendanceDetailModal'
import type {
  Attendance,
  AttendanceStatus,
  MyAttendanceFilterQueryDto,
} from '@/features/attendance/types/attendance.types'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const
/** Radix Select can't hold an empty value, so "no filter" needs a sentinel. */
const ALL = 'all'

type RangePreset = 'week' | 'month' | 'lastMonth' | 'custom'

const RANGE_OPTIONS = [
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
] as const

const STATUS_OPTIONS = (
  Object.keys(ATTENDANCE_STATUS_LABEL) as AttendanceStatus[]
).map((value) => ({ value, label: ATTENDANCE_STATUS_LABEL[value] }))

function presetRange(preset: Exclude<RangePreset, 'custom'>): {
  startDate: string
  endDate: string
} {
  const now = new Date()
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
  if (preset === 'week') {
    return {
      startDate: fmt(startOfWeek(now, { weekStartsOn: 1 })),
      endDate: fmt(endOfWeek(now, { weekStartsOn: 1 })),
    }
  }
  const month = preset === 'month' ? now : subMonths(now, 1)
  return { startDate: fmt(startOfMonth(month)), endDate: fmt(endOfMonth(month)) }
}

/** "1 Oct – 31 Oct" for the record count line. */
function formatRange(startDate?: string, endDate?: string): string | null {
  if (!startDate || !endDate) return null
  return `${formatDate(startDate, 'd MMM')} – ${formatDate(endDate, 'd MMM')}`
}

interface FilterSelectProps {
  label: string
  value: string | undefined
  allLabel: string
  options: { value: string; label: string }[]
  onChange: (value: string | undefined) => void
}

/** Compact inline select for the filter line, sized to sit beside segmented controls. */
function FilterSelect({
  label,
  value,
  allLabel,
  options,
  onChange,
}: FilterSelectProps): React.ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <SelectRoot
        value={value ?? ALL}
        onValueChange={(next) => onChange(next === ALL ? undefined : next)}
      >
        <SelectTrigger
          aria-label={`Filter by ${label.toLowerCase()}`}
          className={cn(
            'h-8 min-w-32 gap-1.5 rounded-lg border-gray-300 pl-2.5 pr-2 text-xs font-medium text-gray-700 dark:text-gray-200',
            value && 'border-primary/40 ring-2 ring-primary/10'
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
    </div>
  )
}

/** "7:00 PM → 6:00 PM", with a "+1d" marker when the shift runs past midnight. */
function ClockSpan({ row }: { row: Attendance }): React.ReactElement {
  const live = isCurrentlyClockedIn(row)
  if (!row.clockIn) return <span className="text-gray-400 dark:text-gray-500">—</span>
  const offset = dayOffset(row.clockIn, row.clockOut)
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums text-gray-900 dark:text-gray-100">
      {formatDate(row.clockIn, 'p')}
      <span className="text-gray-400 dark:text-gray-500" aria-label="to">
        →
      </span>
      {row.clockOut ? (
        <>
          {formatDate(row.clockOut, 'p')}
          {offset > 0 && (
            <span
              className="text-[11px] font-medium text-gray-500 dark:text-gray-400"
              title="Clocked out the next day"
            >
              +{offset}d
            </span>
          )}
        </>
      ) : live ? (
        <span className="inline-flex items-center gap-1.5 font-medium text-green-700 dark:text-green-400">
          <span className="relative flex h-1.5 w-1.5" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
          </span>
          On the clock
        </span>
      ) : (
        <span className="text-gray-400 dark:text-gray-500">No clock-out</span>
      )}
    </span>
  )
}

/** Scheduled shift under the clock times, so lateness reads at a glance. */
function ScheduledSpan({ row }: { row: Attendance }): React.ReactElement {
  const schedule = getScheduledWindow(row)
  if (!schedule) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">Unscheduled</span>
  }
  return (
    <span
      className="whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-gray-400"
      title="Scheduled shift"
    >
      Shift {formatDate(schedule.start.toISOString(), 'p')} – {formatDate(schedule.end.toISOString(), 'p')}
      {schedule.overnight && ' +1d'}
    </span>
  )
}

export function WorkspaceAttendancePage(): React.ReactElement {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [range, setRange] = useState<RangePreset>('month')
  const [dates, setDates] = useState(() => presetRange('month'))
  const [status, setStatus] = useState<AttendanceStatus | undefined>()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const filter = useWorkspaceCompanyFilter({ onChange: () => setPage(1) })
  const [selected, setSelected] = useState<Attendance | null>(null)

  useEffect(() => {
    const trimmed = searchInput.trim()
    if (trimmed === search) return undefined
    const id = window.setTimeout(() => {
      setSearch(trimmed)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput, search])

  const query = useMemo<MyAttendanceFilterQueryDto>(
    () => ({
      startDate: dates.startDate || undefined,
      endDate: dates.endDate || undefined,
      status,
      search: search || undefined,
      page,
      limit,
      companyIds: filter.companyIds,
    }),
    [dates, status, search, page, limit, filter.companyIds]
  )

  const { data, isLoading, error, refetch } = useAttendanceWorkspace(query)
  const records = useMemo(() => data?.data ?? [], [data?.data])
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const companyById = useMemo(() => {
    const map = new Map<string, { name: string; color?: string }>()
    for (const company of filter.companies) {
      map.set(company._id, { name: company.name, color: company.color })
    }
    return map
  }, [filter.companies])

  const hasActiveFilters =
    filter.selectedCompanyIds.size > 0 ||
    range !== 'month' ||
    !!status ||
    !!searchInput.trim()

  const handleRangeChange = (next: RangePreset): void => {
    setRange(next)
    if (next !== 'custom') setDates(presetRange(next))
    setPage(1)
  }

  const handleDateChange = (key: 'startDate' | 'endDate', value: string): void => {
    setDates((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const handleResetFilters = (): void => {
    filter.reset()
    setRange('month')
    setDates(presetRange('month'))
    setStatus(undefined)
    setSearchInput('')
    setSearch('')
    setPage(1)
  }

  const openRecord = useCallback((row: Attendance) => {
    if (!row.companyId) return
    setSelected(row)
  }, [])

  /** Pointer convenience; the row's action carries the same behaviour for keyboards. */
  const handleRowActivate = (row: Attendance): void => {
    if (window.getSelection()?.toString()) return
    openRecord(row)
  }

  /**
   * Everything a row shows, derived once so the table and the mobile card list
   * can never drift apart.
   */
  const describeRecord = (row: Attendance) => {
    const name = attendanceUserName(row)
    const email = row.user?.email?.trim()
    const company = row.companyId ? companyById.get(row.companyId) : undefined
    return {
      name,
      // Secondary line only when it adds something the name doesn't already say.
      meta: row.user?.employeeId ?? (email && email !== name ? email : null),
      company,
      companyColor: getCompanyColor(row.companyId ?? '', company?.color),
      shiftDate: row.date ? formatDate(row.date.slice(0, 10), 'EEE, d MMM') : '—',
      worked: row.clockOut ? formatWorkedMinutes(row.totalWorkedMinutes) : null,
      deduction: row.deductions?.totalDeduction ?? 0,
    }
  }

  const renderPerson = (
    row: ReturnType<typeof describeRecord>,
    size: 'md' | 'lg'
  ): React.ReactElement => (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={row.name} size={size} tone="soft" />
      <div className="min-w-0">
        <p className="truncate font-medium text-gray-900 dark:text-gray-100">{row.name}</p>
        {row.meta ? (
          <p className="truncate text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {row.meta}
          </p>
        ) : null}
      </div>
    </div>
  )

  const renderCompany = (row: ReturnType<typeof describeRecord>): React.ReactElement => (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: row.companyColor.dot }}
      />
      <span className="truncate text-gray-700 dark:text-gray-300">
        {row.company?.name ?? '—'}
      </span>
    </span>
  )

  const renderStatus = (record: Attendance, size?: 'sm'): React.ReactElement => (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Badge variant={attendanceStatusBadgeVariant(record.status)} dot size={size}>
        {ATTENDANCE_STATUS_LABEL[record.status] ?? record.status}
      </Badge>
      {record.lateByMinutes > 0 && (
        <span
          className="text-[11px] font-medium tabular-nums text-amber-700 dark:text-amber-400"
          title={`Clocked in ${record.lateByMinutes} minutes late`}
        >
          +{record.lateByMinutes}m
        </span>
      )}
    </span>
  )

  const renderWorked = (row: ReturnType<typeof describeRecord>): React.ReactElement =>
    row.worked ? (
      <span className="tabular-nums text-gray-900 dark:text-gray-100">{row.worked}</span>
    ) : (
      <span className="text-gray-400 dark:text-gray-500">—</span>
    )

  const renderAction = (
    record: Attendance,
    row: ReturnType<typeof describeRecord>
  ): React.ReactElement => (
    <TableRowActions>
      <TableRowAction
        icon={Eye}
        label={`View attendance for ${row.name} on ${row.shiftDate}`}
        onSelect={() => openRecord(record)}
      />
    </TableRowActions>
  )

  const renderTable = (): React.ReactElement => (
    <DataTable className="hidden md:block" tableClassName="w-full" caption="Attendance records">
      <DataTableHead>
        <tr>
          <DataTableHeadCell className="w-[25%] px-6 py-3">Employee</DataTableHeadCell>
          <DataTableHeadCell className="w-[16%] px-6 py-3">Company</DataTableHeadCell>
          <DataTableHeadCell className="w-[11%] px-6 py-3">Shift date</DataTableHeadCell>
          <DataTableHeadCell className="w-[19%] px-6 py-3">Clock in → out</DataTableHeadCell>
          <DataTableHeadCell className="w-[8%] px-6 py-3" title="Total worked, excluding breaks">
            Worked
          </DataTableHeadCell>
          <DataTableHeadCell className="w-[11%] px-6 py-3">Status</DataTableHeadCell>
          <DataTableHeadCell align="right" className="w-[6%] px-6 py-3">
            Deductions
          </DataTableHeadCell>
          <DataTableHeadCell align="right" className="w-[4%] px-6 py-3">
            <span className="sr-only">Actions</span>
          </DataTableHeadCell>
        </tr>
      </DataTableHead>
      <DataTableBody>
        {records.map((record) => {
          const row = describeRecord(record)
          return (
            <DataTableRow
              key={record._id}
              className="group/row cursor-pointer"
              onClick={() => handleRowActivate(record)}
            >
              <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                {renderPerson(row, 'md')}
              </DataTableCell>
              <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                {renderCompany(row)}
              </DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">{row.shiftDate}</DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">
                <div className="flex flex-col gap-0.5">
                  <ClockSpan row={record} />
                  <ScheduledSpan row={record} />
                </div>
              </DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">{renderWorked(row)}</DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">{renderStatus(record)}</DataTableCell>
              <DataTableCell align="right" className="px-6 py-3 align-middle tabular-nums">
                {row.deduction !== 0 ? (
                  <span className="font-medium text-red-700 dark:text-red-400">
                    −{formatWorkedMinutes(row.deduction)}
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">—</span>
                )}
              </DataTableCell>
              <DataTableCell align="right" className="px-6 py-3 align-middle">
                {renderAction(record, row)}
              </DataTableCell>
            </DataTableRow>
          )
        })}
      </DataTableBody>
    </DataTable>
  )

  /** Phone layout: each record becomes a card with the same facts stacked. */
  const renderCards = (): React.ReactElement => (
    <ul className="flex flex-col gap-2 md:hidden">
      {records.map((record) => {
        const row = describeRecord(record)
        return (
          <li
            key={record._id}
            className="group/row cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors active:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:active:bg-gray-700/40"
            onClick={() => handleRowActivate(record)}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                {renderPerson(row, 'lg')}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  {renderCompany(row)}
                  <span className="text-gray-500 dark:text-gray-400">{row.shiftDate}</span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                {renderStatus(record, 'sm')}
                {renderAction(record, row)}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 text-sm dark:border-gray-700/60">
              <div className="flex flex-col gap-0.5">
                  <ClockSpan row={record} />
                  <ScheduledSpan row={record} />
                </div>
              <span className="shrink-0 text-xs">{renderWorked(row)}</span>
            </div>
          </li>
        )
      })}
    </ul>
  )

  const rangeLabel = formatRange(dates.startDate, dates.endDate)

  return (
    <>
      <PageShell
        breadcrumbItems={[
          { label: 'Time & Attendance', to: '/dashboard' },
          { label: 'Attendance' },
        ]}
        icon={Clock}
        title="Attendance"
        description="Cross-company attendance across every company you can read."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar filter={<WorkspaceCompanyChips filter={filter} />}>
            <WorkspaceToolbarSearch
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search by name, email, or employee ID…"
              ariaLabel="Search attendance"
              active={Boolean(search)}
            />
          </WorkspaceToolbar>

          {/* One filter line, no card, applied as you choose — same as Users and Members. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Range</span>
              <SegmentedControl
                ariaLabel="Date range"
                size="sm"
                options={[...RANGE_OPTIONS]}
                value={range}
                onChange={(value) => handleRangeChange(value as RangePreset)}
              />
            </div>

            {range === 'custom' && (
              <div className="flex items-center gap-2">
                <DateInput
                  name="attendance-from"
                  value={dates.startDate}
                  max={dates.endDate || undefined}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  placeholder="From"
                  className="h-8 w-36 rounded-lg px-2.5 text-xs font-medium shadow-sm"
                />
                <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
                <DateInput
                  name="attendance-to"
                  value={dates.endDate}
                  min={dates.startDate || undefined}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                  placeholder="To"
                  className="h-8 w-36 rounded-lg px-2.5 text-xs font-medium shadow-sm"
                />
              </div>
            )}

            <FilterSelect
              label="Status"
              allLabel="All statuses"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(next) => {
                setStatus(next as AttendanceStatus | undefined)
                setPage(1)
              }}
            />

            {hasActiveFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-gray-600 dark:text-gray-400"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                <span className="ml-1">Reset</span>
              </Button>
            ) : null}

            <p className="ml-auto text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
              {isLoading
                ? 'Loading records…'
                : `${total} ${total === 1 ? 'record' : 'records'}${rangeLabel ? ` · ${rangeLabel}` : ''}`}
            </p>
          </div>

          {error ? (
            <QueryErrorState error={error} title="Couldn't load attendance" onRetry={refetch} />
          ) : isLoading ? (
            <DataTableCard>
              <TableSkeleton rows={limit > 10 ? 10 : limit} cols={8} />
            </DataTableCard>
          ) : records.length === 0 ? (
            <EmptyState
              icon={Clock}
              title={hasActiveFilters ? 'No attendance matches these filters' : 'No attendance this month'}
              description={
                hasActiveFilters
                  ? 'Try a different range, company, or status.'
                  : 'Records appear here as staff clock in to their shifts.'
              }
              action={
                hasActiveFilters ? (
                  <Button variant="secondary" size="sm" onClick={handleResetFilters}>
                    Reset filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              {renderCards()}
              {renderTable()}

              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onLimitChange={(next) => {
                  setLimit(next)
                  setPage(1)
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                className="px-6 py-3"
              />
            </>
          )}
        </div>
      </PageShell>

      <AttendanceDetailModal
        companyId={selected?.companyId ?? ''}
        attendanceId={selected?._id ?? null}
        companyName={
          selected?.companyId ? filter.companyNameById.get(selected.companyId) : undefined
        }
        user={selected?.user ?? null}
        onClose={() => setSelected(null)}
      />
    </>
  )
}

WorkspaceAttendancePage.displayName = 'WorkspaceAttendancePage'
